import React from 'react';

export type PrimaryButtonVariant = 
  | 'default'
  | 'bordered' 
  | 'clear' 
  | 'inverse' 
  | 'danger'
  | 'danger_clear';

export interface PrimaryButtonProps {
  icon?: React.ComponentType<any>;
  title: string;
  isDisabled?: boolean;
  onClick: () => void;
  style?: React.CSSProperties;
  className?: string;
  variant?: PrimaryButtonVariant;
  fullWidth?: boolean;
  align?: 'spread' | 'center'; // spread: text left, icon right | center: everything centered
  iconPosition?: 'left' | 'right'; // Position of icon relative to text
  size?: 'default' | 'small' | 'xsmall'; // Size variant - default is 48px (3rem), small is 40px (2.5rem), xsmall is 32px (2rem)
}

export const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  icon: Icon,
  title,
  isDisabled = false,
  onClick,
  style,
  className = '',
  variant = 'default',
  fullWidth = true,
  align = 'spread',
  iconPosition = 'right',
  size = 'default'
}) => {
  const handleClick = () => {
    if (!isDisabled) {
      onClick();
    }
  };

  const buttonClasses = [
    'core-primary-button',
    `core-primary-button--${variant}`,
    `core-primary-button--align-${align}`,
    `core-primary-button--size-${size}`,
    isDisabled && 'core-primary-button--disabled',
    fullWidth && 'core-primary-button--full-width',
    Icon && `core-primary-button--has-icon`,
    Icon && `core-primary-button--icon-${iconPosition}`,
    className
  ].filter(Boolean).join(' ');

  // Render icon component
  const iconElement = Icon && (
    <Icon 
      size={size === 'xsmall' ? 14 : size === 'small' ? 16 : 20}
      className="core-primary-button__icon"
    />
  );

  // For spread alignment with icon on right (original behavior)
  if (align === 'spread' && Icon && iconPosition === 'right') {
    return (
      <button
        className={buttonClasses}
        onClick={handleClick}
        style={style}
        disabled={isDisabled}
        type="button"
      >
        <div className="core-primary-button__content">
          <div className="core-primary-button__title">
            {title}
          </div>
        </div>
        {iconElement}
      </button>
    );
  }

  // For all other cases (center alignment or icon on left)
  return (
    <button
      className={buttonClasses}
      onClick={handleClick}
      style={style}
      disabled={isDisabled}
      type="button"
    >
      <div className="core-primary-button__content">
        {Icon && iconPosition === 'left' && iconElement}
        <div className="core-primary-button__title">
          {title}
        </div>
        {Icon && iconPosition === 'right' && align === 'center' && iconElement}
      </div>
    </button>
  );
};
